/**
 * The navigation session: where the visitor is, what the automaton made of it,
 * and — the part that costs money — WHEN the guide is told.
 *
 * **The position is never streamed.** Coordinates already ride on every run in
 * `context.latitude/longitude` (`App.tsx`), so `watchPosition` just keeps
 * `sessionStore.geo` fresh and costs no request at all. What this store decides
 * is which of the automaton's signals becomes a RUN, and the budget is real:
 * nginx rate-limits run starts across the whole stand (30r/m, burst 10), so a
 * dozen visitors walking a park must not each fire an event per GPS fix.
 *
 * Four rules, in the order they bite (design §4.2):
 *
 *  1. **`nav_stop` always goes.** It is a tap, not a detection — a visitor
 *     asking to stop being led may not be rate-limited, and may not be dropped
 *     either. It bypasses the interval; it does NOT bypass the turn in flight,
 *     because `App.send` refuses a second start and the message would vanish
 *     without a trace. So it QUEUES like everything else and outranks whatever
 *     was queued: an arrival waiting behind a long turn is stale news next to
 *     «перестань меня вести».
 *  2. **One event per minute** otherwise.
 *  3. **A run is already in flight → the event is DEFERRED, not dropped.**
 *     `App` refuses a second start anyway, and losing the arrival at Монплезир
 *     because the guide was still answering about tickets is the whole reason
 *     this is a queue of one rather than an `if`.
 *  4. **One deferred event at a time, and the newest wins.** Two arrivals
 *     queued behind one long turn would arrive as a pair the moment it ended,
 *     which is a newsletter.
 *
 * Nothing here survives an F5, deliberately: `watchPosition` does not either,
 * the browser permission has to be asked for again by a GESTURE on iOS, and a
 * store that remembered «we were following» would show a stale position as a
 * live one.
 */

import { create } from 'zustand';

import type { A2uiAction } from '../a2ui/actions';
import type { LngLat } from '../map/objects';
import { dispatchTap } from '../a2ui/actions';
import { useSurface } from '../a2ui/surfaceStore';
import { useChat } from '../store/chatStore';
import { arrivedAction, offRouteAction, stopAction } from './events';
import {
  DEFAULT_TRACK_CONFIG,
  initialTrackState,
  step,
  syncCurrent,
  type Fix,
  type RouteModel,
  type TrackState,
} from './track';

/** No more than one detected event a minute. `nav_stop` is exempt. */
export const MIN_EVENT_INTERVAL_MS = 60_000;

export type SheetState = 'collapsed' | 'expanded';

export type NavState = {
  /** Is the camera glued to the visitor? Off at the first manual gesture. */
  following: boolean;
  /** Is `watchPosition` running? Started only by an explicit tap. */
  watching: boolean;
  last: Fix | null;
  track: TrackState;
  sheet: SheetState;
  /** A run failed and the visitor pressed «Стоп» twice — leave locally. */
  forcedExit: boolean;
  lastEventAt: number;
  /** Waiting for the turn in flight to end. At most one. */
  deferred: { action: A2uiAction; at: number } | null;
  /** «Стоп» was tapped and the board frame has not come back yet. */
  stopping: boolean;
  /**
   * A point the overlay asked the map to centre on, consumed by `MapLive`.
   *
   * A REQUEST rather than a call, because the map has exactly one writer: the
   * overlay is DOM over a canvas it has no handle on, and «who moved the
   * camera» keeps one answer. Cleared by whoever honours it.
   */
  centreOn: LngLat | null;

  ingest: (fix: Fix, route: RouteModel) => void;
  /** The board's `current` — the `n` being walked towards. */
  syncCurrent: (route: RouteModel, current: number | null | undefined) => void;
  setFollowing: (on: boolean) => void;
  setWatching: (on: boolean) => void;
  setSheet: (sheet: SheetState) => void;
  /** The «Стоп» button. Second press after a failed run leaves locally. */
  requestStop: () => void;
  /**
   * The guide stopped leading — the board took `map:nav` away.
   *
   * A whole session ends here, not just a flag: `nav_stop` swaps the card for a
   * `map:plan` in `card` mode, and everything this store holds is about the
   * walk that just finished. Left standing, `stopping` would make the first
   * «Стоп» of the NEXT session read as its second press (the escape hatch for a
   * failed run), `watching` would claim a watch that unmounted with the
   * overlay, and `arrived` would hold ids that make a re-navigated route look
   * finished before it starts. A queued event goes with them: the session it
   * was about is over.
   *
   * A no-op when there is nothing to end, because the selector that calls it
   * runs on every board frame.
   */
  endSession: () => void;
  /** «Show me this stop». Turns following off — the visitor is looking away. */
  requestCentre: (at: LngLat) => void;
  takeCentre: () => LngLat | null;
  /** Sends whatever was waiting for the turn to end, if it is still worth it. */
  flush: () => void;
  reset: () => void;
};

const EMPTY = () => ({
  following: true,
  watching: false,
  last: null as Fix | null,
  track: initialTrackState(),
  sheet: 'collapsed' as SheetState,
  forcedExit: false,
  lastEventAt: 0,
  deferred: null as { action: A2uiAction; at: number } | null,
  stopping: false,
  centreOn: null as LngLat | null,
});

/** The board's own surface id, for the tap envelope. */
function surfaceId(): string | null {
  return useSurface.getState().surfaceId;
}

export const useNav = create<NavState>((set, get) => ({
  ...EMPTY(),

  ingest: (fix, route) => {
    const state = get();
    const track = step(state.track, route, fix, DEFAULT_TRACK_CONFIG);
    // A fix worse than the accuracy ceiling comes back by identity — it moved
    // nothing, so it writes nothing, and `last` is not updated either: the
    // «я здесь» marker must not jump 200 m on a bad reading.
    if (track === state.track) return;

    set({ track, last: fix });

    const signal = track.emit;
    if (!signal) return;

    if (signal.kind === 'back_on_route') {
      // The visitor came back before the turn in flight ended, so the queued
      // «Кажется, я не на маршруте» is about a problem that has since resolved
      // itself. Telling the guide about it now is worse than saying nothing:
      // it arrives with no indication of the delay and reads as current.
      const queued = get().deferred;
      if (queued && queued.action.event.name === 'nav_off_route') set({ deferred: null });
      return;
    }

    if (signal.kind === 'arrived') {
      const stop = route.stops[signal.index];
      if (!stop) return;
      queue(get, set, arrivedAction(stop, signal.distanceM, fix), fix.at);
      return;
    }

    const nearest = route.stops.find((stop) => stop.id === signal.nearestStopId) ?? null;
    queue(get, set, offRouteAction(signal.distanceM, nearest, fix), fix.at);
  },

  syncCurrent: (route, current) => {
    const merged = syncCurrent(get().track, route, current);
    if (merged !== get().track) set({ track: merged });
  },

  setFollowing: (on) => set({ following: on }),

  setWatching: (on) => set({ watching: on, ...(on ? { following: true } : {}) }),

  setSheet: (sheet) => set({ sheet }),

  clearForcedExit: () => {
    if (get().forcedExit) set({ forcedExit: false });
  },

  endSession: () => {
    const state = get();
    const live =
      state.forcedExit ||
      state.watching ||
      state.stopping ||
      state.deferred !== null ||
      state.last !== null ||
      state.track.arrived.length > 0;
    if (live) set(EMPTY());
  },

  requestCentre: (at) => set({ centreOn: at, following: false }),

  takeCentre: () => {
    const at = get().centreOn;
    if (at) set({ centreOn: null });
    return at;
  },

  requestStop: () => {
    const state = get();
    // The mode is the BOARD's to leave. The second press is the escape hatch
    // for a run that failed: a 500 must not trap somebody full-screen.
    if (state.stopping && useChat.getState().error) {
      set({ forcedExit: true, stopping: false, watching: false, deferred: null });
      return;
    }
    set({ stopping: true });
    const action = stopAction(state.last);

    // **Not sent unconditionally, which is what it used to be.** `App.send`
    // opens with `if (status !== 'idle') return`, so a «Стоп» tapped while the
    // guide was still answering went into that early return and was gone — no
    // request, no error, and a button that simply re-enabled itself when the
    // unrelated turn finished. It queues now, and it REPLACES whatever was
    // queued: this is the visitor speaking, and an arrival detected two minutes
    // ago is not news beside it.
    if (useChat.getState().status !== 'idle') {
      set({ deferred: { action, at: Date.now() } });
      return;
    }
    set({ lastEventAt: Date.now(), deferred: null });
    dispatchTap(
      { userMessage: String(action.event.userMessage), action: action as unknown as Record<string, unknown> },
      'nav:stop',
      surfaceId(),
    );
  },

  flush: () => {
    const state = get();
    if (!state.deferred) return;
    if (useChat.getState().status !== 'idle') return;
    const { action } = state.deferred;
    set({ deferred: null, lastEventAt: Date.now() });
    dispatchTap(
      { userMessage: String(action.event.userMessage), action: action as unknown as Record<string, unknown> },
      action.event.name === 'nav_stop' ? 'nav:stop' : 'nav:event',
      surfaceId(),
    );
  },

  reset: () => set(EMPTY()),
}));

/**
 * The rate limit and the deferral, in one place so the two detections cannot
 * grow different policies.
 *
 * `at` is the FIX's own timestamp, not `Date.now()`: a backgrounded tab
 * delivers a burst of catch-up fixes, and a limiter clocked on the wall would
 * let all of them through at once.
 */
function queue(
  get: () => NavState,
  set: (patch: Partial<NavState>) => void,
  action: A2uiAction,
  at: number,
): void {
  const state = get();
  if (at - state.lastEventAt < MIN_EVENT_INTERVAL_MS) return;
  // A tap the visitor made outranks anything this module detected on its own.
  if (state.deferred?.action.event.name === 'nav_stop') return;
  if (useChat.getState().status !== 'idle') {
    // Kept, not dropped — and only the newest, or a long turn ends in a volley.
    set({ deferred: { action, at } });
    return;
  }
  set({ lastEventAt: at, deferred: null });
  dispatchTap(
    { userMessage: String(action.event.userMessage), action: action as unknown as Record<string, unknown> },
    'nav:event',
    surfaceId(),
  );
}
